import React, { SyntheticEvent, useEffect, useRef } from 'react'
import { IWorksheetCard } from './worksheetCard/interfaces';
import * as S from './wsPreviewStyle'; 
import CloseButton from './close-button.svg';
import { usePrintableWorksheetsState } from 'printableWorksheets/state';
import PrintableWorksheetsEvents from 'printableWorksheets/events';
import { canDownloadWs, freeWsDownloadLeft, hasSignedUpFromWs, isFlutterUser, timeout, totalWsDownloadCount } from 'apps/teacher/config/common';
import { GRADE_TITLE_MAP } from 'printableWorksheets/constants';

declare let window: any;

interface IWSPreviewProps {
    worksheetDetails: IWorksheetCard | null;
    showWorksheetPreview: boolean;
    setShowWorksheetPreview: (value: boolean) => void;
    showUnlockButton?: boolean
}

const WSPreview: React.FC<IWSPreviewProps> = (props) => {
    const { userId } = usePrintableWorksheetsState();
    const overlayRef = useRef(null);
    const gradesAndStandard = props.worksheetDetails?.grades;
    const subjectsAndTopics = props.worksheetDetails?.subject_and_topics;
    const wsDownloadLeft = JSON.parse(localStorage.getItem('free_ws_download_left'));
    const isUserSubscribed = JSON.parse(localStorage.getItem('is_user_subscribed'));
    
    const popupCloseHandler = () => {
        document.querySelector('#ws-preview-wrapper').classList.add('slide-down');
        setTimeout(() => {
            const eventProps = {
                clickedOn: 'Close',
                freeWorksheetsLeftCount: freeWsDownloadLeft()
            }
            PrintableWorksheetsEvents.interactedWorksheetPreview(eventProps);
            props.setShowWorksheetPreview(false);
            if(window.parent?.triggerIsFlutterHeaderClickDisabled) {
                window.parent.triggerIsFlutterHeaderClickDisabled();
            }
            /// if preview popup is closed then again set overflow properties in parent. (Safari/Firefox browser issue fix)
            const worksheetsRightDiv = window.document.getElementById('ws-right-div');
            worksheetsRightDiv.style.overflowX = 'hidden';
            worksheetsRightDiv.style.overflowY = 'overflow';

            // delete key after WS preview is shown via signup from WS Detail pages
            localStorage.removeItem('viaWsOnboarding');
        }, 300);
    }

    // If clicked outside of PreviewWrapper, close the popup
    const handleClickOutside = (event: any) => {
        if (overlayRef.current && !overlayRef.current.contains(event.target)) {
          popupCloseHandler();
        }
    };

    // Function to close the preview popup
    const closePreviewPopup = () => {
        props.setShowWorksheetPreview(false);
    };

    function handlePreviewPopupClose() {
        closePreviewPopup(); // Update the state to close the popup
    }

    useEffect(() => {
        const props = {
            from: hasSignedUpFromWs() ? 'Onboarding' : 'Printables Tab',
            freeWorksheetsLeftCount: freeWsDownloadLeft()
        }
        PrintableWorksheetsEvents.viewedWorksheetPreview(props);
        document.addEventListener('mousedown', handleClickOutside);

        // Add an event listener to listen for the custom event dispatched from web-ui repo and re-render the component
        window.parent.parent.addEventListener('closePreviewPopup', handlePreviewPopupClose);
    
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
          window.parent.parent.removeEventListener('closePreviewPopup', handlePreviewPopupClose);
        };
    }, []);

    const downloadWorksheet = (e: SyntheticEvent, from: string) => {
        e.preventDefault();
        if (props.worksheetDetails.id && userId && canDownloadWs()) {
            fetch(props.worksheetDetails.pdf_url, {
                credentials: 'omit'
            }).then((res) => res.blob()).then((blob) => { // Open WS pdf in new tab
                const isChromeBrowser = navigator.userAgent.includes("Chrome");
                // setTimeout is used for Safari browser as pdf was not opening in next window. On chrome, setTimeout is blocking new window popup
                isChromeBrowser ? 
                    window.open(props.worksheetDetails.pdf_url, '_blank')
                :
                    setTimeout(() => {
                        window.open(props.worksheetDetails.pdf_url, '_blank');
                    });
                const eventProps = {
                    clickedOn: from,
                    freeWorksheetsLeftCount: freeWsDownloadLeft()
                }
                PrintableWorksheetsEvents.interactedWorksheetPreview(eventProps);
                PrintableWorksheetsEvents.downloadedWorksheetB2c({
                    worksheetName: props.worksheetDetails.title,
                    worksheetCode: props.worksheetDetails.code
                });
                !isUserSubscribed && wsDownloadLeft > 0 && localStorage.setItem('free_ws_download_left', JSON.stringify(wsDownloadLeft - 1));
                // sending this event from FE as worksheet download handling is done on FE only.
                if((localStorage.getItem('free_ws_download_left') === "0") && !isUserSubscribed){
                    PrintableWorksheetsEvents.reachedDailyLimitBackend({
                        contentName: props.worksheetDetails.title,
                        contentCode: props.worksheetDetails.code,
                        contentType: "worksheets",
                        from: "Worksheets"
                    })
                }
                props.setShowWorksheetPreview(false);
                if(window.parent.triggerIsFlutterHeaderClickDisabled) {
                    window.parent.triggerIsFlutterHeaderClickDisabled();
                }
                // delete key after WS preview is shown via signup from WS Detail pages
                localStorage.removeItem('viaWsOnboarding');

                // Using window.parent.parent.location.reload() for flutter users -> There are 2 top layers - React(web-ui) -> Flutter -> React(school-ui)
                isFlutterUser() ? reloadFlutter() :  window.location.reload();
            }).catch((error) => {
                console.log(error);
            });
        }
    }

    const reloadFlutter = () => {
        // used window.parent.parent because Printables is opened in webview. There are 2 top layers above it.
        window.parent.parent.location.reload();
    } 

    const redirectToPurchase = () => {
        isFlutterUser() ?
        window.parent.parent.location.href = '/dashboard#/purchase?source=printables&limit=0' :
        location.hash = '/purchase?source=printables&limit=0';
    }

    return (
        <S.PreviewOverlay>
            <S.PreviewWrapper id="ws-preview-wrapper" isOpen={props.showWorksheetPreview} ref={overlayRef} isFlutterUser={isFlutterUser()}>
                <S.CloseButton onClick={popupCloseHandler} isOpen={props.showWorksheetPreview}>
                    <CloseButton />
                    <S.ButtonText>Close Preview</S.ButtonText>
                </S.CloseButton>
                <S.WSDetails>
                    <S.WSTextWrapper>
                        <S.WSTitle>{props.worksheetDetails?.title}</S.WSTitle>
                        <S.WSDescription>{props.worksheetDetails?.description}</S.WSDescription>
                        {JSON.parse(localStorage.getItem('showWsCountText')) && <S.FreeWSCount>You have {JSON.parse(freeWsDownloadLeft() || '0')} of {totalWsDownloadCount()} free worksheets left for today</S.FreeWSCount>}
                        {
                            props.showUnlockButton ?
                                <S.CtaButton onClick={() => redirectToPurchase()}>Start free trial to unlock</S.CtaButton>
                            :
                                <S.CtaButton onClick={(e) => downloadWorksheet(e, 'Print CTA')}>Print worksheet</S.CtaButton>
                        }
                        <S.TagsCategory>GRADE & STANDARD</S.TagsCategory>
                        <S.TagsWrapper>
                            {gradesAndStandard?.map((name: string, index: number) => {
                                return (
                                    <S.TagName key={index}>{JSON.parse(localStorage.getItem('viaWsOnboarding')) ? name : GRADE_TITLE_MAP[name]}</S.TagName>
                                )
                            })}
                        </S.TagsWrapper>

                        <S.TagsCategory>SUBJECTS & TOPICS</S.TagsCategory>
                        <S.TagsWrapper>
                            {subjectsAndTopics?.map((name, index) => {
                                return (
                                    <S.TagName key={index}>{name}</S.TagName>
                                )
                            })}
                        </S.TagsWrapper>
                    </S.WSTextWrapper>
                    <S.WSImageWrapper>
                        <S.WSImage onClick={(e) => downloadWorksheet(e, 'Thumbnail')}>
                            <S.ImageOverlay />
                            <img src={props.worksheetDetails?.image_url} alt="worksheetImage"/>
                        </S.WSImage>
                    </S.WSImageWrapper>
                </S.WSDetails>
            </S.PreviewWrapper>
        </S.PreviewOverlay>
    ) 
}

export default WSPreview